import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import LockLine from '@/assets/icons/lock_line.svg?component';
import MailLine from '@/assets/icons/mail_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import { Spinner } from '@/components/atoms/Spinner';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import TextField from '@/components/atoms/TextField';

import type { LoginValues } from '../useLoginFlow';

import publicStyles from '../../public.module.scss';
import styles from '../Login.module.scss';

interface LoginPasswordStepProps {
    values: LoginValues;
    setValues: (values: LoginValues) => void;
    loading: boolean;
    onSubmit: (e: FormEvent) => void;
    onUseDifferentEmail: () => void;
}

export default function LoginPasswordStep(props: LoginPasswordStepProps) {
    const { values, setValues, loading, onSubmit, onUseDifferentEmail } = props;
    const { t } = useTranslation();

    return (
        <form onSubmit={onSubmit}>
            <FormField
                label={t('publicPages.common.email')}
                labelClassName={publicStyles.inverseLabel}>
                <TextField
                    prefix={<SvgIcon icon={MailLine} />}
                    groupClassName={publicStyles.inputGroup}
                    adornmentClassName={publicStyles.inputGroupText}
                    type="email"
                    name="email"
                    value={values.email}
                    className={publicStyles.formControl}
                    disabled
                />
            </FormField>
            <FormField
                label={t('publicPages.common.password')}
                labelClassName={publicStyles.inverseLabel}>
                <TextField
                    prefix={<SvgIcon icon={LockLine} />}
                    groupClassName={publicStyles.inputGroup}
                    adornmentClassName={publicStyles.inputGroupText}
                    type="password"
                    name="password"
                    placeholder={t('publicPages.common.password')}
                    value={values.password}
                    className={publicStyles.formControl}
                    onChange={(e) =>
                        setValues({
                            ...values,
                            password: e.target.value,
                        })
                    }
                />
            </FormField>
            <div className={classNames(publicStyles.actions, styles.actionsCenter)}>
                {loading ? (
                    <div className={styles.submitting}>
                        <Spinner size="lg" fullScreen className={styles.loader} />
                    </div>
                ) : (
                    <Button size={'large'} type="submit" fullWidth disabled={!values.password}>
                        {t('publicPages.login.login')}
                    </Button>
                )}
            </div>
            <div className={styles.secondaryAction}>
                <Button variant="link" className={styles.inverseLink} onClick={onUseDifferentEmail}>
                    {t('publicPages.login.useADifferentEmail')}
                </Button>
            </div>
            <div className={styles.resetLink}>
                <Link to="/reset-password-request2">{t('publicPages.common.resetPassword')}</Link>
            </div>
        </form>
    );
}
